import { useState, useMemo } from 'react'
import { Search, RefreshCw, History, X } from 'lucide-react'
import { clsx } from 'clsx'
import type { ProductChange } from '@api/client'

interface Props {
  history: ProductChange[]
  loading: boolean
  onRefresh: () => void
}

const CHANGE_TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  price: { bg: 'bg-primary/20', text: 'text-primary', label: 'Price' },
  name: { bg: 'bg-info/20', text: 'text-info', label: 'Name' },
  vendor: { bg: 'bg-info/20', text: 'text-info', label: 'Vendor' },
  create: { bg: 'bg-success/20', text: 'text-success', label: 'Create' },
  delete: { bg: 'bg-danger/20', text: 'text-danger', label: 'Delete' },
  bulk_upload: { bg: 'bg-warning/20', text: 'text-warning', label: 'Upload' },
  bulk_replace: { bg: 'bg-danger/20', text: 'text-danger', label: 'Replace' },
}

function formatDate(isoString: string): string {
  try {
    const d = new Date(isoString + 'Z')
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return isoString
  }
}

function formatEmail(email: string): string {
  return email.split('@')[0].replace('.', ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function ChangeHistoryPanel({ history, loading, onRefresh }: Props) {
  const [searchTerm, setSearchTerm] = useState('')

  const filtered = useMemo(() => {
    if (!searchTerm) return history
    const term = searchTerm.toLowerCase()
    return history.filter(h =>
      h.details.toLowerCase().includes(term) ||
      h.user_email.toLowerCase().includes(term) ||
      h.change_type.toLowerCase().includes(term)
    )
  }, [history, searchTerm])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search & Refresh */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search history..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X size={12} />
            </button>
          )}
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-[var(--text-secondary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      {/* History List */}
      <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
            <History size={48} strokeWidth={1} className="mb-4 opacity-50" />
            <p className="text-sm">No change history</p>
            <p className="text-[11px] text-gray-600 mt-1">
              {searchTerm ? 'No results match your search' : 'Changes will appear here once products are modified'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-color)] max-h-[calc(100vh-380px)] overflow-y-auto">
            {filtered.map(entry => {
              const style = CHANGE_TYPE_STYLES[entry.change_type] || { bg: 'bg-gray-500/20', text: 'text-gray-400', label: entry.change_type }
              return (
                <div key={entry.id} className="px-4 py-3 hover:bg-[var(--bg-secondary)]/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={clsx('text-[9px] px-1.5 py-0.5 rounded font-bold uppercase', style.bg, style.text)}>
                          {style.label}
                        </span>
                        <span className="text-[11px] font-medium text-[var(--text-primary)]">
                          {formatEmail(entry.user_email)}
                        </span>
                      </div>
                      <p className="text-[11px] text-[var(--text-secondary)] whitespace-pre-wrap break-words">
                        {entry.details}
                      </p>
                    </div>
                    <span className="text-[10px] text-[var(--text-muted)] whitespace-nowrap shrink-0">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
